/**
 * Тонкий API клиент для бэкенда.
 *
 * Авторизация: добавляем X-Telegram-Init-Data header автоматически.
 */

import type { MeResponse, TokenResponse, DexStatus } from '@memepad/shared';

const BASE = import.meta.env.VITE_API_BASE || '';

function tgInitData(): string {
  return window.Telegram?.WebApp?.initData ?? '';
}

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-Telegram-Init-Data', tgInitData());
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const r = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(`API ${r.status}: ${text}`);
  }
  return r.json() as Promise<T>;
}

export async function syncMe(args: {
  privyUserId?: string;
  tonAddress?: string;
}): Promise<MeResponse> {
  return request<MeResponse>('/api/me', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function getToken(masterAddr: string): Promise<TokenResponse> {
  return request<TokenResponse>(`/api/token/${encodeURIComponent(masterAddr)}`);
}

/**
 * CC-057 — persist the connected TonConnect wallet address server-side.
 * TonConnect keeps the address only in client state; without this call
 * `users.ton_address` stays NULL and recipient-fee routing falls back to
 * the temp custody wallet. Called once per address change from Wallet.tsx.
 */
export async function connectWallet(tonAddress: string): Promise<void> {
  await request<{ ok: true }>('/api/me/wallet/connect', {
    method: 'POST',
    body: JSON.stringify({ tonAddress }),
  });
}

/** CC-057 — clear the server-side wallet address on TonConnect disconnect. */
export async function disconnectWallet(): Promise<void> {
  await request<{ ok: true }>('/api/me/wallet/disconnect', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Recent tokens for the discovery feed.
 *
 * CC-015 PR-B: by default the API excludes dev-factory tokens. Pass
 * `includeTest: true` to opt in (used by the dev miniapp build, not by
 * the prod UI).
 */
export async function getRecentTokens(opts: {
  limit?: number;
  includeTest?: boolean;
} = {}): Promise<{
  tokens: Array<{
    ticker: string;
    masterAddr: string;
    curveAddr: string;
    imageCid: string;
    realTonReserves: string;
    /** CC-025: per-token graduation target. Required by the Home feed's
     *  progress bar — without it the UI would fall back to a hardcoded
     *  number (the original bug). Bigint-serialised as a decimal string
     *  by the bot's JSON middleware. */
    targetTon: string;
    isGraduated: boolean;
    /** CC-027: dex/migration lifecycle — drives the migration badge.
     *  null on legacy rows predating the dex_status column. */
    dexStatus: DexStatus | null;
    deployedAt: string;
  }>;
}> {
  const limit = opts.limit ?? 20;
  const params = new URLSearchParams({ limit: String(limit) });
  if (opts.includeTest) params.set('includeTest', 'true');
  return request(`/api/tokens/recent?${params.toString()}`);
}

// === Royalty recipient flow ===

export interface RoyaltyToken {
  id: number;
  ticker: string;
  name: string;
  imageCid: string;
  masterAddr: string;
  curveAddr: string;
  deployedAt: string;
  /** null for tokens deployed with a raw numeric `for <id>` recipient. */
  intendedUsername: string | null;
  /**
   * CC-052-E — live unclaimed recipient-pool TON fees on the curve, in
   * nano-TON (string). Was `accruedFees` before the cohort-4 dual-receiver
   * split; renamed to disambiguate from the jetton accumulator below.
   */
  accruedTonFees: string;
  /**
   * CC-052-E — accrued jetton LP fees held by the bot's DB accumulator,
   * in jetton base units (string). Auto-sent to the recipient's wallet on
   * the next claim. `'0'` when nothing has accrued.
   */
  accruedJettonFees: string;
  /** CC-052-E — TEP-64 symbol of the accrued jetton (for the `$TICKER` label). */
  jettonSymbol: string;
  /** CC-052-E — decimals of the accrued jetton (9 for memepad jettons). */
  jettonDecimals: number;
}

/**
 * Every token for which the current Telegram user is the royalty recipient.
 *
 * CC-077 — `claimThresholdNano` / `claimGasNano` are the claim economics
 * (bigint decimal strings): the minimum accrued TON to allow a custody
 * claim, and the gas deducted from it. The UI uses them for the per-row
 * threshold hint and the net (after-gas) amount.
 */
export async function getMyRoyalties(): Promise<{
  tokens: RoyaltyToken[];
  claimThresholdNano: string;
  claimGasNano: string;
}> {
  return request('/api/me/royalties');
}

/** One side of a claim-all result. `amount` is a bigint decimal string. */
export interface ClaimSide {
  status:
    | 'claimed'
    | 'nothing'
    | 'skipped'
    | 'unavailable'
    | 'below_threshold'
    | 'failed';
  /** For `claimed`: the NET amount received (gas deducted). Decimal string. */
  amount?: string;
  txHash?: string;
  error?: string;
  /** For `below_threshold`: how much HAS accrued / the minimum needed. */
  accrued?: string;
  threshold?: string;
}

/**
 * CC-075 — unified claim. Settles BOTH of a token's recipient fee streams
 * in one backend call:
 *   - `ton`    — the bonding-curve recipient TON fees (swept out of the
 *                curve via the TEMP_CUSTODY wallet);
 *   - `jetton` — the accumulated LP-fee jettons (paid from the accumulator).
 *
 * Either side may be claimed, empty, skipped, or fail independently —
 * `partial: true` flags a mixed outcome. `request` throws on a non-2xx
 * response (e.g. 502 when everything attempted failed).
 */
export async function claimAll(tokenId: number): Promise<{
  ok: true;
  partial: boolean;
  ton: ClaimSide;
  jetton: ClaimSide;
}> {
  return request(`/api/me/claim-all/${tokenId}`, { method: 'POST' });
}
